import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import moment from 'moment';
import ChatRoom from 'components/ChatRoom';
import People from 'components/People';
import Topics from 'components/Topics';
import Agenda from 'components/Agenda';
import Content from 'components/Content';
import ElementsHall from 'components/ElementsHall';
import ArrowBackIcon from 'assets/images/icons/arrow_back_button.svg';
import CallIcon from 'assets/images/icons/call_white.svg';
import ArrowRight from 'assets/images/grey_arrow_right.svg';
import Countdown from 'components/Countdown';
import { CHAT_TYPE_CALL_FOR_IMPACT } from 'constants/chat';
import { getURLByPhase } from 'utils/calls';
import { DESKTOP_WIDTH, TABLET_WIDTH } from 'constants/breakPoints';
import {
  CallHall, ChatTab, ContentTab, DocIframe,
} from './styles';

function CallForImpactHall(props) {
  const {
    literals, app, setBreadcrumb, calls, resetAll, chats,
    match: { params: { section: defaultSection, id } }, history,
  } = props;

  const data = _.find(calls, { id: parseInt(id, 10) });

  const [chat, setChat] = useState(null);
  const [collapseChat, setCollapseChat] = useState(false);

  const handleCollapseChat = () => {
    setCollapseChat(!collapseChat);
  };

  useEffect(() => {
    const callForImpactChat = _.find(chats, { element_related: { type_id: CHAT_TYPE_CALL_FOR_IMPACT, id: data.id } });
    setChat({ ...callForImpactChat });
  }, []);

  const showChatInLateral = () => {
    if (window.outerWidth < DESKTOP_WIDTH) {
      return false;
    }

    return true;
  };

  // Breadcrumb
  useEffect(() => {
    const breadcrumb = [
      {
        type: 'back',
      },
      {
        color: '#ffffff',
        text: data ? data.name : literals.networkingHall,
      },
    ];

    setBreadcrumb(breadcrumb.slice());

    resetAll();
  }, [calls, id]);

  // Events
  const goToCall = () => {
    history.push(getURLByPhase(data));
  };

  const goBack = () => {
    history.push('/initiatives');
  };

  const toggleCollapse = () => {
    setCollapseChat(!collapseChat);
  };

  // Handle tabs
  const [selectedTab, setSelectedTab] = useState(defaultSection || 'content');

  const selectTab = (event) => {
    const { currentTarget } = event;
    const section = currentTarget.getAttribute('data-section');
    history.push(`/calls/${id}/hall/${section}`);
    setSelectedTab(section);
  };

  const printChatTab = () => {
    if (!chat) {
      return null;
    }
    return (<ChatRoom key={chat.id} chatId={chat.id} isTab />);
  };

  const openDoc = () => {
    const urlDoc = `https://docs.google.com/document/d/${data.conclusions_document_id}/edit?usp=sharing`;
    window.open(urlDoc);
  };

  const printContentTab = () => {
    let agenda = null;
    const content = (<Content narratives={data.narratives} content={data.content} />);

    if (data.meetings.length || data.speed_datings.length) {
      agenda = (
        <Agenda meetings={data.meetings} speedDatings={data.speed_datings} />
      );
    }

    const showChat = showChatInLateral() && chat;

    const urlDoc = `https://docs.google.com/document/d/${data.conclusions_document_id}/edit?usp=sharing&overridemobile=true&fd=true&rm=minimal&headers=false`;
    const conclusionsDoc = (
      <React.Fragment>
        <div className='title_section'>
          {literals.conclusionsAndInsights}
          <div className='button_header' onClick={openDoc}>
            <div className='icon_button_header'><i className='fas fa-external-link-alt' /></div>
            <div className='placeholder_button_header'>{literals.fullEdit}</div>
          </div>
        </div>
        <DocIframe width='100%' height='119rem' scrolling='yes' src={urlDoc} />
      </React.Fragment>
    );

    return (
      <ContentTab className={showChat ? 'with_chat' : ''} style={{ gridTemplateColumns: collapseChat ? '100% 0%' : '59% 39%', gap: collapseChat ? '0' : '2rem' }}>
        <div className='content_wrapper'>
          <div className='content_layer'>
            {agenda}
            {content}
            {conclusionsDoc}
          </div>
        </div>
        {showChat && !collapseChat && (
          <div className='chat_lateral'>
            <ChatRoom key={chat.id} chatId={chat.id} onCollapse={toggleCollapse} />
          </div>
        )}
      </ContentTab>
    );
  };

  const printCurrentTab = () => {
    switch (selectedTab) {
      case 'people':
        return <People elementType='call_for_impact' elementId={id} />;
      case 'topics':
        return <Topics elementType='call_for_impact' elementId={id} />;
      case 'chat':
        return printChatTab();
      case 'content':
        return printContentTab();
      case app.main_element:
        return <ElementsHall type={app.main_element} elementType='call_for_impact' elementId={id} />;
      default:
        return null;
    }
  };

  const [dateEvent, setDateEvent] = useState(null);

  useEffect(() => {
    let minDateEvent = null;
    const today = moment().format('YYYY-MM-DD');

    data.meetings.forEach((meeting) => {
      const meetingDay = moment(meeting.start_date, 'X').format('YYYY-MM-DD');
      if (meetingDay >= today && (!minDateEvent || meeting.start_date < minDateEvent)) {
        minDateEvent = meeting.start_date;
      }
    });

    data.speed_datings.forEach((meeting) => {
      const meetingDay = moment(meeting.start_date, 'X').format('YYYY-MM-DD');
      if (meetingDay >= today && (!minDateEvent || meeting.start_date < minDateEvent)) {
        minDateEvent = meeting.start_date;
      }
    });

    setDateEvent(minDateEvent);
  }, [calls, id]);

  const showHeader = window.outerWidth > TABLET_WIDTH || selectedTab !== 'chat';

  return (
    <CallHall>
      {showHeader ? (
        <div className='header' style={{ backgroundImage: `url(${data.image})` }}>
          <div className='opacity' />
          <div className='content'>
            <div className='name'>
              <img src={ArrowBackIcon} className='clickable' alt='' onClick={goBack} />
              <img src={CallIcon} className='call_icon' alt='' />
              {data.name}
            </div>
            <div className='description'>{data.description}</div>
            {dateEvent ? (
              <React.Fragment>
                <div className='time_limit'>
                  {`${literals.liveEvent}: ${moment(dateEvent, 'X').format('DD-MM-YYYY')}`}
                </div>
                <Countdown time={dateEvent} />
              </React.Fragment>
            ) : null}
          </div>
          <div className='buttons'>
            <button className='primary' onClick={goToCall}>{literals.start}</button>
          </div>
        </div>
      ) : null}
      <div>
        <div className='tabs inline'>
          <div className={`tab ${selectedTab === 'content' ? 'selected' : ''}`} data-section='content' onClick={selectTab}>{literals.contentTag}</div>
          {chat && !showChatInLateral() ? (
            <div className={`tab ${selectedTab === 'chat' ? 'selected' : ''}`} data-section='chat' onClick={selectTab}>{literals.chat}</div>
          ) : null}
          <div className={`tab ${selectedTab === 'topics' ? 'selected' : ''}`} data-section='topics' onClick={selectTab}>{literals.topics}</div>
          <div className={`tab ${selectedTab === 'people' ? 'selected' : ''}`} data-section='people' onClick={selectTab}>{literals.people}</div>
          {collapseChat && (
            <div className='expand' onClick={handleCollapseChat}>
              <button type='button'>
                <img src={ArrowRight} alt='' />
                <p>{literals.chat}</p>
              </button>
            </div>
          )}
        </div>
        <div className='tab_content'>{printCurrentTab()}</div>
      </div>
    </CallHall>
  );
}


CallForImpactHall.propTypes = {
  literals: PropTypes.object.isRequired,
  app: PropTypes.object.isRequired,
  setBreadcrumb: PropTypes.func.isRequired,
  calls: PropTypes.array,
  chats: PropTypes.array,
  resetAll: PropTypes.func.isRequired,
};

CallForImpactHall.defaultProps = {
  calls: [],
  chats: [],
};

export default CallForImpactHall;



// WEBPACK FOOTER //
// ./src/routes/CallForImpactHall/components/index.jsx